import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2 } from 'lucide-react';

interface RenameDialogProps {
  isOpen: boolean;
  title: string;
  currentName: string;
  onConfirm: (newName: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export default function RenameDialog({
  isOpen,
  title,
  currentName,
  onConfirm,
  onCancel,
  isLoading = false,
}: RenameDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(currentName);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 当对话框打开时，重置名称并聚焦输入框
  useEffect(() => {
    if (isOpen) {
      setName(currentName);
      // 延迟聚焦，确保 DOM 已渲染，光标定位到文本末尾（而非全选）
      setTimeout(() => {
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.focus();
          const len = textarea.value.length;
          textarea.setSelectionRange(len, len);
          // 滚动到底部，确保光标可见（修复重复打开时不滚动的问题）
          textarea.scrollTop = textarea.scrollHeight;
        }
      }, 50);
    }
  }, [isOpen, currentName]);

  // ESC 键关闭（loading 时禁止关闭）
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onCancel, isLoading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    const trimmedName = name.trim();
    if (trimmedName && trimmedName !== currentName) {
      onConfirm(trimmedName);
    } else if (!trimmedName) {
      // 名称为空，不允许提交
      textareaRef.current?.focus();
    } else {
      // 名称没有变化，直接关闭
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* 遮罩层 */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
        onClick={isLoading ? undefined : onCancel}
      />

      {/* 对话框 */}
      <div className="fixed inset-0 flex items-center justify-center z-[9999] px-4">
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-md"
          onClick={e => e.stopPropagation()}
        >
          {/* 标题栏 */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={20} />
            </button>
          </div>

          {/* 内容 */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4">
              <textarea
                ref={textareaRef}
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={isLoading}
                placeholder={t('common.enterName')}
                maxLength={255}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed resize-none"
              />
              <div
                className={`text-xs mt-1 text-right ${name.length >= 255 ? 'text-red-500' : 'text-gray-400'}`}
              >
                {name.length}/255
              </div>
            </div>

            {/* 按钮 */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={!name.trim() || isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {t('common.confirm')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
